import { AbstractListLoader } from './abstract-list-loader';
import { Observable } from 'rxjs';
import { InfinitLookupResult } from './infinit-lookup-result';
import { ColumnSortModel, TableSortModel } from '../../tablesort/tableSortModel';
import { LoadingState } from './loading-state';

export class ListLoaderInfinite<T> extends AbstractListLoader<T> {
  allItems = new Array<T>();
  currentLoadingOffset = 0;
  chunkSize: number;
  lookupItemFunc: (offset: number, limit: number) => Observable<InfinitLookupResult<T>>;
  publishItemsWhenAllLoaded: boolean;
  private sort_col: ColumnSortModel<any>;

  constructor(builder: ListLoaderInfiniteBuilder<T>) {
    super(builder.pageSize);
    this.chunkSize = builder.chunkSize;
    this.lookupItemFunc = builder.lookupItemFunc;
    this.stateSubject.next(LoadingState.INITIAL_LOADING);
    this.loadData();
    this.internalChangePage(builder.currentPage);
    this.publishItemsWhenAllLoaded = builder.publishItemsWhenAllLoaded;
  }

  changePage(page: number) {
    if (this.currentPage === page) {
      return;
    }
    this.internalChangePage(page);
  }

  get resumable(): boolean {
    return this.state === LoadingState.SUSPENDED;
  }

  get stoppable(): boolean {
    return this.state === LoadingState.INITIAL_LOADING;
  }

  get isSuspended(): boolean {
    return this.stateSubject.value === LoadingState.SUSPENDED;
  }

  resume() {
    this.stateSubject.next(LoadingState.INFINITE_LOADING);
    this.loadData();
  }

  suspend() {
    this.unsubscribeAll();
    this.stateSubject.next(LoadingState.SUSPENDED);
  }

  applySortModel(tableSortModel: TableSortModel<T>): void {
    this.tableSortModel = tableSortModel;
    const sub = this.tableSortModel.sortedColumns$.subscribe((colModel) => {
      if (!colModel) {
        return;
      } else if (this.stateSubject.value !== LoadingState.ALL_DATA_LOADED) {
        this.allItems = colModel.sort(this.allItems);
        this.internalChangePage(this.currentPage);
      }
      this.sort_col = colModel;
    });
    this.subscriptions.push(sub);
  }

  protected internalChangePage(page: number) {
    this.currentPage = page;
    const start = this.pagerData.startIndex;
    const deleteCount = this.pagerData.endIndex;
    const paggedItems = this.allItems.slice(start, deleteCount);
    if (this.publishItemsWhenAllLoaded && this.stateSubject.value !== LoadingState.ALL_DATA_LOADED) {
      return;
    }
    this.itemsSubject.next(paggedItems);
  }

  private loadData() {
    const sub = this.lookupItemFunc(this.currentLoadingOffset, this.chunkSize).subscribe({
      next: (response) => {
        if (!response.lastJunk) {
          this.currentLoadingOffset = this.currentLoadingOffset + this.chunkSize;
          this.allItems.push(...response.result);
          if (!this.publishItemsWhenAllLoaded) {
            this.totalItemsVisible = this.allItems.length;
            this.internalChangePage(this.currentPage);
            this.stateSubject.next(LoadingState.INFINITE_LOADING);
          }
          this.loadData();
        } else {
          this.totalItemsVisible = this.allItems.length;

          this.stateSubject.next(LoadingState.ALL_DATA_LOADED);
          this.internalChangePage(this.currentPage);
        }
        if (this.sort_col) {
          this.allItems = this.sort_col.sort(this.allItems);
        }
      },
      error: (err) => this.filterAccessDenied(err)
    });
    this.subscriptions.push(sub);
  }
}

export class ListLoaderInfiniteBuilder<T> {
  private _lookupItemFunc: (offset: number, limit: number) => Observable<InfinitLookupResult<T>>;
  private _currentPage: number;
  private _pageSize: number;
  private _chunkSize: number;
  private _publishItemsWhenAllLoaded = false;

  static builder<U>(): ListLoaderInfiniteBuilder<U> {
    return new ListLoaderInfiniteBuilder<U>();
  }

  setLookupItemFunc(value: (offset: number, limit: number) => Observable<InfinitLookupResult<T>>): ListLoaderInfiniteBuilder<T> {
    this._lookupItemFunc = value;
    return this;
  }

  get lookupItemFunc(): (offset: number, limit: number) => Observable<InfinitLookupResult<T>> {
    return this._lookupItemFunc;
  }

  setPageSize(value: number): ListLoaderInfiniteBuilder<T> {
    this._pageSize = value;
    return this;
  }

  get pageSize(): number {
    return this._pageSize;
  }

  setCurrentPage(value: number): ListLoaderInfiniteBuilder<T> {
    this._currentPage = value;
    return this;
  }

  get currentPage(): number {
    return this._currentPage;
  }

  setChunkSize(value: number): ListLoaderInfiniteBuilder<T> {
    this._chunkSize = value;
    return this;
  }

  get chunkSize(): number {
    return this._chunkSize;
  }

  setPublishItemsWhenAllLoaded(value: boolean): ListLoaderInfiniteBuilder<T> {
    this._publishItemsWhenAllLoaded = value;
    return this;
  }

  get publishItemsWhenAllLoaded(): boolean {
    return this._publishItemsWhenAllLoaded;
  }

  build(): ListLoaderInfinite<T> {
    return new ListLoaderInfinite<T>(this);
  }
}
